import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { publishableKey: String, createCheckoutSessionUrl: String }

  // INTENT: Mount Stripe Embedded Checkout component safely on DOM connection.
  // EXPECTED OUTCOME: Fetches client secret from backend and mounts iframe, or displays a friendly retry message on failure.
  // CAVEAT/TRAP: Non-200 responses or missing clientSecret throw unhandled IntegrationError in Stripe JS if not guarded.
  async connect() {
    const csrfToken = document.head?.querySelector(`meta[name="csrf-token"]`)?.getAttribute("content") || "";

    try {
      const stripe = await Stripe(this.publishableKeyValue);
      const response = await fetch(this.createCheckoutSessionUrlValue, {
        method: "POST",
        headers: {
          accept: "application/json",
          'content-type': 'application/json',
          "X-CSRF-Token": csrfToken
        },
      });

      if (!response.ok) {
        this.renderErrorMessage();
        return;
      }

      const data = await response.json();
      const clientSecret = data && data.clientSecret;

      if (!clientSecret) {
        this.renderErrorMessage();
        return;
      }

      const checkout = await stripe.initEmbeddedCheckout({
        clientSecret,
      });

      checkout.mount(this.element);
    } catch (e) {
      this.renderErrorMessage();
    }
  }

  // INTENT: Provide user feedback if checkout initialization fails.
  // EXPECTED OUTCOME: Replaces blank container with a clear message and retry prompt announced via ARIA live region semantics.
  renderErrorMessage() {
    this.element.innerHTML = `<div role="alert" aria-live="polite" class="p-4 text-center text-red-600 bg-red-50 rounded-lg border border-red-200"><p class="font-medium">Unable to load checkout.</p><p class="text-sm text-red-500 mt-1">Please refresh the page to try again.</p></div>`;
  }
}
